/**
 * Clients Page
 *
 * CRUD interface for managing client records.
 */

import React, { useState } from 'react';
import { Card, Stack, Flex, Button, Input, Modal, useTheme, spacing, textStyles, fontSize } from '@fti/design-system';
import { useClients, useClientMutations } from '../../api/clients';
import { useHierarchy } from '../../context/HierarchyContext';
import { LoadingState } from '../shared/LoadingState';
import { ErrorDisplay } from '../shared/ErrorDisplay';
import { ConfirmModal } from '../shared/ConfirmModal';
import type { Client } from '../../api/types';
import { RoleGate } from '../shared/RoleGate';
import { GATES } from '../../auth/roles';
import { errorMessage } from '../../api/errors';

function ClientFormModal({ isOpen, onClose, onSave, initialName, initialCode, title }: {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string, code: string) => Promise<void>;
  initialName: string;
  initialCode: string;
  title: string;
}) {
  const { theme } = useTheme();
  const [name, setName] = useState(initialName);
  const [code, setCode] = useState(initialCode);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const borderColor = theme.colors.border.default;

  const inputStyle: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: '4px',
    border: `1px solid ${borderColor}`,
    background: theme.colors.background.paper,
    color: textColor,
    fontSize: fontSize.sm,
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setErrorMsg('');
    try {
      await onSave(name.trim(), code.trim());
    } catch (err: any) {
      setErrorMsg(err?.message || 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Modal.Header>
        <h3 style={{ margin: 0, ...textStyles.label, color: textColor }}>{title}</h3>
      </Modal.Header>
      <Modal.Body>
        <Stack gap="sm">
          <div>
            <label style={{ fontSize: fontSize.xs, color: mutedColor, display: 'block', marginBottom: '4px' }}>Client Name</label>
            <input placeholder="Client name" value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: fontSize.xs, color: mutedColor, display: 'block', marginBottom: '4px' }}>Client Code</label>
            <input placeholder="Client code" value={code} maxLength={6} onChange={(e) => setCode(e.target.value)} style={inputStyle} />
            <span style={{ fontSize: fontSize.xs, color: mutedColor, display: 'block', marginTop: '4px' }}>
              Client code must be 6 characters (e.g. A24937).
            </span>
          </div>
          {errorMsg && <span style={{ color: theme.colors.semantic.error.default, fontSize: fontSize.xs }}>{errorMsg}</span>}
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <Flex gap="sm" justify="end">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="solid" color="primary" size="sm" onClick={handleSave} disabled={!name.trim() || saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Flex>
      </Modal.Footer>
    </Modal>
  );
}

export function ClientsPage() {
  const { theme } = useTheme();
  const { data: clients, loading, error, refetch } = useClients({ limit: 100, orderBy: 'name', ascending: true });
  const { createClient, updateClient, deleteClient } = useClientMutations();
  const { selectClient, bumpData } = useHierarchy();

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Client | null>(null);
  const [deleteBlockedMsg, setDeleteBlockedMsg] = useState('');
  const [search, setSearch] = useState('');

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const borderColor = theme.colors.border.default;

  const filteredClients = clients?.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.clientcode?.toLowerCase().includes(search.toLowerCase())
  );

  const handleSaveNew = async (name: string, code: string) => {
    await createClient({ name, clientcode: code });
    setShowAddModal(false);
    refetch();
    bumpData();
  };

  const handleSaveEdit = async (name: string, code: string) => {
    if (!editingClient) return;
    await updateClient(editingClient.id, { name, clientcode: code });
    setEditingClient(null);
    refetch();
    bumpData();
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteClient(deleteTarget.id);
      setDeleteTarget(null);
      refetch();
    bumpData();
    } catch (err: unknown) {
      // The service explains its own refusal — naming how many children block the
      // delete — so show that rather than a guess. The hardcoded text below is a
      // fallback only: it happens to be right for the common case, which is exactly
      // what makes substituting it dangerous. A 409 raised for any other reason
      // would be reported as a child-record problem the user cannot find.
      setDeleteBlockedMsg(
        errorMessage(err, 'Cannot delete: this client has matters. Remove all matters first.')
      );
    }
  };

  if (loading) return <LoadingState message="Loading clients..." />;
  if (error) return <ErrorDisplay error={error} onRetry={refetch} />;

  return (
    <Stack gap="lg">
      {/* Page Header */}
      <Flex justify="between" align="center">
        <div>
          <h1 style={{ ...textStyles.h3, color: textColor }}>Clients</h1>
          <p style={{ margin: 0, marginTop: spacing.xs, fontSize: fontSize.sm, color: mutedColor }}>
            Manage client records
          </p>
        </div>
        <RoleGate minRole={GATES.hierarchyWrite}>
          <Button variant="solid" color="primary" size="sm" onClick={() => setShowAddModal(true)}>
            + Add Client
          </Button>
        </RoleGate>
      </Flex>

      {/* Search */}
      <Input
        placeholder="Search clients by name or code..."
        value={search}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
      />

      {/* Client Table */}
      <Card variant="elevated">
        <Card.Body>
          {filteredClients && filteredClients.length > 0 ? (
            <div style={{ border: `1px solid ${borderColor}`, borderRadius: '8px', overflow: 'hidden' }}>
              {/* Table Header */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 90px 150px 100px',
                padding: `${spacing.sm} ${spacing.md}`,
                background: theme.colors.background.paper,
                borderBottom: `1px solid ${borderColor}`,
                fontSize: fontSize.xs,
                fontWeight: 600,
                color: mutedColor,
                textTransform: 'uppercase' as const,
              }}>
                <span>Name</span>
                <span>id_client</span>
                <span>Code</span>
                <span style={{ textAlign: 'right' }}>Actions</span>
              </div>

              {/* Table Rows */}
              {filteredClients.map((client) => (
                <div
                  key={client.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 90px 150px 100px',
                    padding: `${spacing.sm} ${spacing.md}`,
                    borderBottom: `1px solid ${borderColor}`,
                    alignItems: 'center',
                    fontSize: fontSize.sm,
                    cursor: 'pointer',
                  }}
                  onClick={() => selectClient(client)}
                >
                  <span style={{ fontWeight: 500, color: textColor }}>{client.name}</span>
                  <span style={{ color: mutedColor, fontFamily: 'monospace', fontSize: fontSize.xs }}>
                    {client.id}
                  </span>
                  <span style={{ color: mutedColor, fontFamily: 'monospace', fontSize: fontSize.xs }}>
                    {client.clientcode}
                  </span>
                  <Flex gap="xs" justify="end" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                    <RoleGate minRole={GATES.hierarchyWrite}>
                      <Button variant="ghost" size="sm" onClick={() => setEditingClient(client)}>Edit</Button>
                      <Button variant="ghost" size="sm" color="error" onClick={() => { setDeleteTarget(client); setDeleteBlockedMsg(''); }}>
                        Delete
                      </Button>
                    </RoleGate>
                  </Flex>
                </div>
              ))}
            </div>
          ) : (
            <Flex direction="column" align="center" gap="sm" style={{ padding: spacing.lg }}>
              <span style={{ color: mutedColor, fontSize: fontSize.sm }}>
                {search ? 'No clients match your search' : 'No clients found'}
              </span>
            </Flex>
          )}
        </Card.Body>
      </Card>

      {/* Add Modal */}
      {showAddModal && (
        <ClientFormModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSave={handleSaveNew}
          initialName=""
          initialCode=""
          title="Add Client"
        />
      )}

      {/* Edit Modal */}
      {editingClient && (
        <ClientFormModal
          isOpen={!!editingClient}
          onClose={() => setEditingClient(null)}
          onSave={handleSaveEdit}
          initialName={editingClient.name}
          initialCode={editingClient.clientcode || ''}
          title="Edit Client"
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Delete Client"
        message={deleteBlockedMsg || `Are you sure you want to delete "${deleteTarget?.name}"? This cannot be undone.`}
        confirmLabel={deleteBlockedMsg ? 'OK' : 'Delete'}
        variant={deleteBlockedMsg ? 'warning' : 'danger'}
        onConfirm={deleteBlockedMsg ? () => setDeleteTarget(null) : handleDeleteConfirm}
        onCancel={() => { setDeleteTarget(null); setDeleteBlockedMsg(''); }}
      />
    </Stack>
  );
}
